<template>
  <div class="modern-table-page">
    <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.type_to_search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.search_for') + '...'"
        />
      </div>

      <div class="filter-group">
      </div>

      <button class="reload-btn" @click="resetReload" :title="$t('globals.reload')">
        <icon name="sync-alt" :spin="getAllParkingInvoicesLoading"></icon>
      </button>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key"
              :class="{ sortable: field.sortable }"
              @click="field.sortable && handleSort(field.key)"
            >
              {{ field.label || field.key }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in paginatedItems">
            <tr
              :key="item.id"
              class="table-row"
              @click="rowClicked(item)"
              :class="{ 'row-expanded': item._showDetails }"
            >
              <td>{{ item.id }}</td>
              <td>{{ item.invoice_no }}</td>
              <td>
                <span class="status-badge" :class="`status-${item.status}`">
                  {{ item.status }}
                </span>
              </td>
              <td v-b-tooltip.hover :title="item.sent_at">
                {{ item.sent_at ? item.sent_at.substring(0, 10) : '-' }}
              </td>
              <td>{{ item.sent_to || '-' }}</td>
              <td>
                <icon v-if="item.is_paid" name="regular/check-square" class="paid-icon"></icon>
                <icon v-else name="regular/square" class="unpaid-icon"></icon>
              </td>
              <td style="text-align: right;">{{ currencyFormat(item.total_price, item.currency) }}</td>
              <td style="text-align: right;">{{ currencyFormat(item.total_price_ex_vat, item.currency) }}</td>
              <td>{{ item.company_name || '-' }}</td>
              <td>{{ item.user_name || '-' }}</td>
            </tr>
            <tr v-if="item._showDetails" :key="`${item.id}-details`" class="details-row">
              <td :colspan="fields.length" class="details-cell">
                <div class="details-content">
                  <div class="details-actions">
                    <b-button
                      size="sm"
                      variant="info"
                      @click.stop="toCustomer(item.customer_id)"
                    >
                      {{ $t("globals.customer") }}
                    </b-button>
                    <b-button
                      size="sm"
                      variant="info"
                      @click.stop="openPdf(item.id)"
                    >
                      .pdf
                    </b-button>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="getAllParkingInvoicesLoading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>
      <div v-else-if="filteredItems.length === 0" class="empty-state">
        <p>{{ $t("globals.no_results") }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.show") }}</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="per-page-select"
        />
        <span class="pagination-info">
          {{ paginationText }}
        </span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-btn"
          :class="{ active: page === currentPage }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapMutations, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue")
  },
  data() {
    return {
      filter: null,
      currentPage: 1,
      perPage: 20,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        {
          key: "id",
          sortable: true
        },
        {
          key: "invoice_no",
          label: this.$t("globals.invoice_number"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "sent_at",
          label: this.$t("globals.sent_at"),
          sortable: true
        },
        {
          key: "sent_to",
          label: this.$t("globals.sent_to"),
          sortable: true
        },
        {
          key: "is_paid",
          label: this.$t("globals.paid"),
          sortable: true
        },
        {
          key: "total_price",
          label: this.$t("globals.total"),
          sortable: true
        },
        {
          key: "total_price_ex_vat",
          label: this.$t("globals.total_price_no_vat"),
          sortable: true
        },
        {
          key: "company_name",
          label: this.$t("globals.company"),
          sortable: true
        },
        {
          key: "user_name",
          label: this.$t("globals.user"),
          sortable: true
        }
      ]
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleMutation"]),
    ...mapActions("invoices", ["fetchAllParkingInvoices"]),
    currencyFormat,
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    toCustomer(id) {
      this.$router.push({ name: "customer", params: { id: id } });
    },
    openPdf(id) {
      window.open(
        `${this.getBackendUrl}/pdf_parking_invoices/${id}.pdf?locale=${this.getLocale}`,
        "_blank"
      );
    },
    rowClicked(item) {
      item._rowVariant = null;
      item._showDetails = !item._showDetails;
      if (item._showDetails) {
        item._rowVariant = "info";

        this.$store.state.allParkingInvoices.forEach(invoice => {
          if (item.id == invoice.id) return;
          invoice._showDetails = false;
          invoice._rowVariant = null;
        });
      }
    },
    resetReload() {
      this.filter = null;
      this.currentPage = 1;
      this.fetchAllParkingInvoices();
    }
  },
  computed: {
    ...mapGetters("invoices", ["getAllParkingInvoices", "getAllParkingInvoicesLoading"]),
    ...mapGetters([
      "getBackendUrl",
      "getDefConf",
      "getGraphQlUrl",
      "getLocale",
      "getUserBrandPermissions"
    ]),

    filteredItems() {
      let items = [...this.getAllParkingInvoices];

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        items = items.filter(item => {
          const invoiceNo = item.invoice_no?.toLowerCase() || '';
          const status = item.status?.toLowerCase() || '';
          const companyName = item.company_name?.toLowerCase() || '';
          const userName = item.user_name?.toLowerCase() || '';
          return invoiceNo.includes(searchTerm) ||
                 status.includes(searchTerm) ||
                 companyName.includes(searchTerm) ||
                 userName.includes(searchTerm);
        });
      }

      items.sort((a, b) => {
        let aVal = a[this.sortBy] || '';
        let bVal = b[this.sortBy] || '';

        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return items;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage);
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    },

    paginationText() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.filteredItems.length);
      const total = this.filteredItems.length;
      return `${start}-${end} of ${total}`;
    }
  },
  watch: {
    paginatedItems() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }
    }
  },
  created() {
    this.fetchAllParkingInvoices();
  }
};
</script>

<style scoped>
.details-row {
  background-color: #f8f9fa;
}

.details-cell {
  padding: 1.5rem !important;
}

.details-content {
  display: flex;
  justify-content: flex-end;
}

.details-actions {
  display: flex;
  gap: 0.5rem;
}

.row-expanded {
  background-color: #e9ecef;
}

.paid-icon,
.unpaid-icon {
  color: #5469d4;
}
</style>
